const DATABASE_NAME = "das-offline-challans";
const DATABASE_VERSION = 1;
const CHALLAN_STORE = "challans";
const METADATA_STORE = "metadata";
const PRINT_RESOURCE_CACHE = "das-offline-print-resources-v1";

const getRequestResult = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const getUserScope = (user) => {
  let storedUser = {};

  try {
    storedUser = JSON.parse(localStorage.getItem("userDetails")) || {};
  } catch (error) {
    storedUser = {};
  }

  const currentUser = user || storedUser;
  const identifier =
    currentUser.id ||
    currentUser.user_id ||
    currentUser.email ||
    currentUser.username;

  if (identifier) return String(identifier);

  // This fallback keeps different login tokens isolated even if the stored user
  // object does not expose an id. The token itself is never written to IndexedDB.
  const token = localStorage.getItem("userToken") || "anonymous";
  return `token-${token.slice(-24)}`;
};

const openDatabase = () =>
  new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("This browser does not support IndexedDB."));
      return;
    }

    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;

      if (!database.objectStoreNames.contains(CHALLAN_STORE)) {
        const store = database.createObjectStore(CHALLAN_STORE, {
          keyPath: "cacheKey",
        });
        store.createIndex("scope", "scope", { unique: false });
        store.createIndex("campusId", "campusId", { unique: false });
        store.createIndex("admissionId", "admissionId", { unique: false });
      }

      if (!database.objectStoreNames.contains(METADATA_STORE)) {
        database.createObjectStore(METADATA_STORE, { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const waitForTransaction = (transaction) =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

export const extractChallansFromResponse = (response) => {
  const candidates = [
    response,
    response && response.data,
    response && response.payload,
    response && response.payload && response.payload.data,
    response && response.payload && response.payload.students,
    response && response.payload && response.payload.fee_challans,
  ];

  const list = candidates.find(Array.isArray);
  if (list) return list;

  // Also accept the single-student shape supplied by the API sample.
  if (response && Array.isArray(response.fee_challans_detials)) {
    return [response];
  }

  if (
    response &&
    response.payload &&
    Array.isArray(response.payload.fee_challans_detials)
  ) {
    return [response.payload];
  }

  return [];
};

export const replaceOfflineChallans = async (challans, user) => {
  const database = await openDatabase();
  const scope = getUserScope(user);
  const syncedAt = new Date().toISOString();
  const transaction = database.transaction(
    [CHALLAN_STORE, METADATA_STORE],
    "readwrite",
  );
  const store = transaction.objectStore(CHALLAN_STORE);
  const scopeIndex = store.index("scope");
  const cursorRequest = scopeIndex.openCursor(IDBKeyRange.only(scope));

  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (cursor) {
      cursor.delete();
      cursor.continue();
      return;
    }

    challans.forEach((student) => {
      if (!student || student.id === undefined || student.id === null) return;

      store.put({
        cacheKey: `${scope}:${student.id}`,
        scope,
        studentId: String(student.id),
        campusId: String(student.campus_id || student.campus?.id || ""),
        admissionId: String(student.admission_id || ""),
        studentName: student.name || "",
        syncedAt,
        data: student,
      });
    });

    transaction.objectStore(METADATA_STORE).put({
      key: `sync:${scope}`,
      scope,
      syncedAt,
      count: challans.length,
    });
  };

  await waitForTransaction(transaction);
  database.close();

  return { count: challans.length, syncedAt };
};

export const getOfflineChallans = async (user) => {
  const database = await openDatabase();
  const transaction = database.transaction(CHALLAN_STORE, "readonly");
  const records = await getRequestResult(
    transaction
      .objectStore(CHALLAN_STORE)
      .index("scope")
      .getAll(IDBKeyRange.only(getUserScope(user))),
  );
  database.close();

  return records.map((record) => record.data);
};

export const getOfflineChallanSyncInfo = async (user) => {
  const database = await openDatabase();
  const transaction = database.transaction(METADATA_STORE, "readonly");
  const result = await getRequestResult(
    transaction.objectStore(METADATA_STORE).get(`sync:${getUserScope(user)}`),
  );
  database.close();
  return result || null;
};

export const saveOfflinePrintSettings = async (settings, user) => {
  const database = await openDatabase();
  const transaction = database.transaction(METADATA_STORE, "readwrite");
  transaction.objectStore(METADATA_STORE).put({
    key: `print-settings:${getUserScope(user)}`,
    settings,
    savedAt: new Date().toISOString(),
  });
  await waitForTransaction(transaction);
  database.close();
};

export const getOfflinePrintSettings = async (user) => {
  const database = await openDatabase();
  const transaction = database.transaction(METADATA_STORE, "readonly");
  const result = await getRequestResult(
    transaction
      .objectStore(METADATA_STORE)
      .get(`print-settings:${getUserScope(user)}`),
  );
  database.close();
  return result ? result.settings : null;
};

export const saveOfflineInstructions = async (instructions, user) => {
  const database = await openDatabase();
  const transaction = database.transaction(METADATA_STORE, "readwrite");
  transaction.objectStore(METADATA_STORE).put({
    key: `instructions:${getUserScope(user)}`,
    instructions,
    savedAt: new Date().toISOString(),
  });
  await waitForTransaction(transaction);
  database.close();
};

export const getOfflineInstructions = async (user) => {
  const database = await openDatabase();
  const transaction = database.transaction(METADATA_STORE, "readonly");
  const result = await getRequestResult(
    transaction
      .objectStore(METADATA_STORE)
      .get(`instructions:${getUserScope(user)}`),
  );
  database.close();
  return result ? result.instructions : [];
};

// export const imageUrlToDataUrl = async (url) => {
//   console.log(url, "test url");
//   if (!url || String(url).startsWith("data:")) return url;

//   const response = await fetch(url);
//   if (!response.ok) throw new Error("Unable to download the print logo.");
//   const blob = await response.blob();

//   return new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.onloadend = () => resolve(reader.result);
//     reader.onerror = () => reject(reader.error);
//     reader.readAsDataURL(blob);
//   });
// };

export const imageUrlToDataUrl = async (url) => {
  console.log(url, "test url");

  if (!url || String(url).startsWith("data:")) {
    return url;
  }

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), 10000)
    : null;
  let response;

  try {
    response = await fetch(url, {
      signal: controller ? controller.signal : undefined,
    });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error("Unable to download the print logo.");
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);

    reader.readAsDataURL(blob);
  });
};

export const cacheImageForOffline = async (url) => {
  if (!url || String(url).startsWith("data:")) return url;
  if (!("caches" in window)) {
    throw new Error("This browser does not support offline image caching.");
  }

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), 10000)
    : null;

  try {
    const request = new Request(url, {
      mode: "no-cors",
      signal: controller ? controller.signal : undefined,
    });
    const response = await fetch(request);

    if (!response.ok && response.type !== "opaque") {
      throw new Error("Unable to download the print image.");
    }

    const cache = await window.caches.open(PRINT_RESOURCE_CACHE);
    await cache.put(request, response.clone());
    return url;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};
